import React from "react";
import { Mail, Building } from "lucide-react";
import ContactForm from "../components/ContactForm";
import { useSettings } from "../context/SettingsContext";
import usePageMeta from "../hooks/usePageMeta";
import PageMasthead from "../components/PageMasthead";
import { local } from "../lib/images";

/**
 * CONNECT WITH US — a compact band, then the form.
 *
 * This page went through a sticky floor-to-ceiling split and then an abstract
 * drawn banner, and both were the same mistake: decorating a page whose only job
 * is to be written in. It is now a heading, the two details a visitor needs, and
 * the form at a comfortable measure.
 *
 * The "Based in …" badge that used to sit at the top has gone: it framed the
 * group by where it is rather than by what it does, and the location already
 * lives in the footer. No street address either — the door is not open for
 * drop-ins, so publishing one would only mislead.
 */
export default function ContactPage() {
  const { settings: s } = useSettings();
  usePageMeta(
    "Connect with Us",
    "Collaborations, enquiries, open-data requests and research proposals — write to the Innovative Waste Management Research Group."
  );

  return (
    <div data-testid="contact-page">
      {/* CONNECT — cream panel, photo left. The warmest ground on the site, on
          the one page that is asking the reader to get in touch. */}
      {/* The brass nameplate beside the lab door, with the framed "compost: proof
          that there is life after death" that someone hung next to it.

          This used to be the whole door, and the whole door was the wrong
          picture for this page: the mask resolves on the right, and the right of
          that frame is the hazard board — DANGER, a radiation trefoil, X-RAY
          RADIATION. A page asking people to get in touch opened on a wall of
          warnings. Cropped to the plaque, the same photograph says the opposite
          thing, and it is still the group's own front door. */}
      <PageMasthead
        testId="contact-header"
        tone="cream"
        image={local("iwm-plaque").src}
        imageSrcSet={local("iwm-plaque").srcSet}
        imageAlt="The brass Innovative Waste Management nameplate beside the lab door"
        eyebrow="§ Correspondence · Connect with Us"
        title={<>Engage with <em className="not-italic text-[#A17E3B]">IWM</em>.</>}
        lede="Collaborations, enquiries, open-data requests, and research proposals — all of them land in the same inbox, and all of them get read."
      >
        <div className="mt-9 flex flex-wrap gap-x-12 gap-y-5">
          {s.email && (
            <div>
              <div className="overline mb-2">Email</div>
              {/* Plain text, not a mailto: link — writing to the group goes
                  through the form below, which posts to our own server. */}
              <div className="font-serif text-xl inline-flex items-center gap-2 text-[#1C2722]" data-testid="contact-email">
                <Mail size={17} className="text-[#A17E3B] shrink-0" />
                <span className="select-all break-all">{s.email}</span>
              </div>
            </div>
          )}
          {s.affiliation && (
            <div>
              <div className="overline mb-2">Affiliation</div>
              <div className="font-serif text-xl inline-flex items-center gap-2 text-[#1C2722]" data-testid="contact-affiliation">
                <Building size={17} className="text-[#A17E3B] shrink-0" />
                {s.affiliation}
              </div>
            </div>
          )}
        </div>
      </PageMasthead>

      {/* The form was a column of thin rules floating on the page background,
          which read as an afterthought on the one page whose entire job it is.
          On the ink panel it becomes the anchor of the page — and the heading
          beside it says what actually happens next, which is the question a
          visitor has before they start typing. */}
      <section className="bg-[#1C2722]" data-testid="contact-form-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <h2 className="font-serif text-[clamp(1.9rem,3vw,2.75rem)] leading-[1.05] tracking-tight text-[#F9F8F6]">
              Write to us.
            </h2>
            <div aria-hidden="true" className="mt-6 h-[3px] w-16 bg-[#A17E3B]" />
            <dl className="mt-8 space-y-6">
              <div>
                <dt className="overline !text-[#B8924A] mb-2">Who reads it</dt>
                <dd className="text-[14.5px] leading-relaxed text-[#D1CEC7]">
                  Everything lands in the group&apos;s inbox — not a ticketing system, and not a
                  mailing list you have been added to.
                </dd>
              </div>
              <div>
                <dt className="overline !text-[#B8924A] mb-2">What to include</dt>
                <dd className="text-[14.5px] leading-relaxed text-[#D1CEC7]">
                  If it is a collaboration or a proposal, a paragraph on what you are working on
                  saves a round of emails. Attach anything that helps.
                </dd>
              </div>
              <div>
                <dt className="overline !text-[#B8924A] mb-2">Student enquiries</dt>
                <dd className="text-[14.5px] leading-relaxed text-[#D1CEC7]">
                  Tell us the area you want to work in and roughly when you would start.
                </dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-8">
            <ContactForm variant="full" tone="ink" testIdPrefix="contactpage" />
          </div>
        </div>
      </section>
    </div>
  );
}
